/**
 * 大海龟旅店 — 角色名册、座位、群众演员与环境闲聊数据（纯数据，无引擎代码）。
 * 坐标：地图瓦片 32px；角色以脚底为原点，feetOf(tx, ty) 给出站在某瓦片上的脚底像素坐标。
 */
import type { Facing } from '@/core/types';
import type { MapNpcPlacement, NpcScheduleStep } from '@/core/types/contracts';
import type { ActorDef } from '@/characters/actorDefs';

export const TILE = 32;
export const INN_MAP_ID = 'turtle-inn';

export function feetOf(tx: number, ty: number): { x: number; y: number } {
  return { x: tx * TILE + 16, y: ty * TILE + 28 };
}

/** 角色 id（与 ACTOR_DEFS / 对白 speaker 一致）。 */
export const CAST = {
  player: 'jiang-zichao',
  zhao: 'zhao-chengbo',
  luo: 'luo-dahu',
  wu: 'wu-yong',
  boss: 'inn-boss',
  waiter: 'inn-waiter',
} as const;

/** 显示名（对白框标题、字幕）。 */
export const DISPLAY_NAMES: Record<string, string> = {
  'jiang-zichao': '江子潮',
  'zhao-chengbo': '赵呈博',
  'luo-dahu': '罗大湖',
  'wu-yong': '吴庸',
  'inn-boss': '老板',
  'inn-waiter': '小二',
  'patron-girl-a': '女孩',
  'patron-girl-b': '女孩',
  'patron-elder-a': '老者',
  'patron-elder-b': '老妇',
  'patron-drinker-a': '酒客',
  'patron-drinker-b': '酒客',
  'patron-drinker-c': '酒客',
  'patron-drinker-d': '酒客',
  'patron-drinker-e': '酒客',
  'patron-drinker-f': '酒客',
  'patron-drinker-g': '酒客',
  'patron-sailor-a': '水手',
  'patron-sailor-b': '水手',
  'patron-barfly-a': '酒客',
  'patron-barfly-b': '酒客',
  'patron-crowd-a': '客人',
  'patron-crowd-b': '客人',
  'patron-crowd-c': '客人',
  'patron-crowd-d': '客人',
};

/** 开发期占位色（角色未到货时用来区分人物）：[衣, 肤, 发]。 */
export const DEV_COLOURS: Record<string, [number, number, number]> = {
  'jiang-zichao': [0x8a6a3a, 0xd8a878, 0x2a1a10],
  'zhao-chengbo': [0x6b7c9c, 0xe8d0b0, 0xf0f0f0],
  'luo-dahu': [0x8c2a22, 0xf0b8a8, 0x2a1a10],
  'wu-yong': [0x3b4a3a, 0xc08a60, 0x1a1210],
  'inn-boss': [0xa8743c, 0xb07a50, 0x1a1210],
  'inn-waiter': [0x5a6e8a, 0xc89868, 0x1a1210],
  'patron-girl-a': [0xd88aa0, 0xd8a878, 0x1a1210],
  'patron-girl-b': [0x8ec95a, 0xc09070, 0x1a1210],
  'patron-elder-a': [0x6a5a4a, 0xb08868, 0xd0d0d0],
  'patron-elder-b': [0x4a4a6a, 0xc0a080, 0xc8c8c8],
  'patron-drinker-a': [0x3c7a3a, 0xb07a50, 0x1a1210],
  'patron-drinker-b': [0x2b3a5c, 0xc89868, 0x1a1210],
  'patron-drinker-c': [0x7a5a2a, 0xb07a50, 0x1a1210],
  'patron-drinker-d': [0x8a4a4a, 0xd8a878, 0x1a1210],
  'patron-drinker-e': [0x4a6a5a, 0xb07a50, 0x1a1210],
  'patron-drinker-f': [0x9a8a4a, 0xc09070, 0x1a1210],
  'patron-drinker-g': [0x5a3a6a, 0xb07a50, 0x1a1210],
  'patron-sailor-a': [0x2b5a7c, 0xc08a60, 0x1a1210],
  'patron-sailor-b': [0x1f8f8a, 0xb07a50, 0x1a1210],
  'patron-barfly-a': [0x6a4a2a, 0xd8a878, 0x1a1210],
  'patron-barfly-b': [0x3a5a3a, 0xc09070, 0x1a1210],
  'patron-crowd-a': [0x7a6a5a, 0xb07a50, 0x1a1210],
  'patron-crowd-b': [0x5a7a8a, 0xc89868, 0x1a1210],
  'patron-crowd-c': [0x8a7a3a, 0xd8a878, 0x1a1210],
  'patron-crowd-d': [0x4a5a7a, 0xb07a50, 0x1a1210],
};

/** 争论那张桌子（瓦片 17,11）的四个座位。 */
export const TABLE_A = { tx: 17, ty: 11 };
export const SEATS = {
  /** 吴庸：桌北，面朝下 */
  wu: { ...feetOf(17, 10), facing: 'down' as Facing },
  /** 罗大湖：桌西，面朝右 */
  luo: { ...feetOf(16, 11), facing: 'right' as Facing },
  /** 空位（江子潮）：桌东，面朝左 */
  player: { ...feetOf(18, 11), facing: 'left' as Facing },
  /** 赵呈博：桌南，面朝上 */
  zhao: { ...feetOf(17, 12), facing: 'up' as Facing },
} as const;

/** 吴庸站起来时离开凳子的位置（向北挪 14px）。 */
export const WU_STAND = { x: SEATS.wu.x, y: SEATS.wu.y - 14 };

/** 三位起哄者（各自不同的酒客）。 */
export const HECKLERS = ['patron-drinker-a', 'patron-drinker-d', 'patron-drinker-g'] as const;

/** 众人大笑时一起做表情的人。 */
export const LAUGHERS = [
  'patron-drinker-a', 'patron-drinker-b', 'patron-drinker-c', 'patron-drinker-d', 'patron-drinker-e',
  'patron-drinker-f', 'patron-drinker-g', 'patron-sailor-a', 'patron-sailor-b', 'patron-barfly-a',
  'patron-barfly-b', 'patron-crowd-a', 'patron-crowd-b', 'patron-crowd-c', 'patron-crowd-d', 'inn-waiter',
] as const;

/** 站着的客人：玩家挤过去时会让路。 */
export const CROWD_IDS = ['patron-crowd-a', 'patron-crowd-b', 'patron-crowd-c', 'patron-crowd-d'] as const;

/** 日程里的 walk 目标是世界像素（IActor.walkTo / ScheduleProgram 约定），这里按瓦片书写再换算。 */
const walk = (tx: number, ty: number): NpcScheduleStep => ({ kind: 'walk', ...feetOf(tx, ty) });
/** 坐着的人：只定住朝向；举杯 / 笑 / 说话的身体帧由场景的姿态导演（innPoseDirector）按图集驱动，不再弹「喝酒」气泡。 */
const sitLoop = (facing: Facing): NpcScheduleStep[] => [{ kind: 'face', facing }, { kind: 'wait', ms: 6000 }, { kind: 'loop' }];
const idleLoop = (a: Facing, b: Facing, ms = 3500): NpcScheduleStep[] => [{ kind: 'face', facing: a }, { kind: 'wait', ms }, { kind: 'face', facing: b }, { kind: 'wait', ms }, { kind: 'loop' }];

function npc(id: string, tx: number, ty: number, facing: Facing, extra: Partial<MapNpcPlacement> = {}): MapNpcPlacement {
  const p = feetOf(tx, ty);
  return { id, actor: id, x: p.x, y: p.y, facing, ...extra };
}

/**
 * 店内外的所有 NPC（≥10 名客人）。
 * 门口：女孩子们与年长者避雨；店里：酒客、水手、站着的客人、酒台后的老板、跑堂的小二；
 * 争论桌：罗大湖（华服、涨红）与吴庸（凶悍、嗓门大）。
 */
export const INN_NPCS: MapNpcPlacement[] = [
  // 争论桌
  { id: CAST.luo, actor: CAST.luo, x: SEATS.luo.x, y: SEATS.luo.y, facing: SEATS.luo.facing, schedule: sitLoop('right') },
  { id: CAST.wu, actor: CAST.wu, x: SEATS.wu.x, y: SEATS.wu.y, facing: SEATS.wu.facing, schedule: sitLoop('down') },
  // 酒台
  npc(CAST.boss, 7, 5, 'down', { dialogue: 'inn-talk-boss', schedule: idleLoop('down', 'left', 2600) }),
  npc(CAST.waiter, 11, 8, 'down', {
    schedule: [
      walk(11, 8), { kind: 'wait', ms: 1200 },
      walk(11, 11), walk(15, 14), { kind: 'wait', ms: 900 }, { kind: 'face', facing: 'left' }, { kind: 'wait', ms: 700 },
      walk(20, 12), { kind: 'wait', ms: 1100 }, { kind: 'face', facing: 'up' },
      walk(20, 9), walk(11, 9), { kind: 'loop' },
    ],
  }),
  npc('patron-barfly-a', 5, 7, 'up', { dialogue: 'inn-talk-barfly', schedule: sitLoop('up') }),
  npc('patron-barfly-b', 9, 7, 'up', { schedule: sitLoop('up') }),
  // 门口避雨的人
  npc('patron-girl-a', 13, 17, 'right', { dialogue: 'inn-talk-girls', schedule: idleLoop('right', 'down', 3000) }),
  npc('patron-girl-b', 14, 17, 'left', { dialogue: 'inn-talk-girls', schedule: idleLoop('left', 'down', 2800) }),
  npc('patron-elder-a', 16, 17, 'down', { dialogue: 'inn-talk-elders', schedule: idleLoop('down', 'left', 4200) }),
  npc('patron-elder-b', 17, 17, 'left', { dialogue: 'inn-talk-elders', schedule: idleLoop('left', 'down', 3800) }),
  // 各桌酒客
  npc('patron-drinker-a', 6, 9, 'right', { dialogue: 'inn-talk-drinker', schedule: sitLoop('right') }),
  npc('patron-drinker-b', 8, 9, 'left', { schedule: sitLoop('left') }),
  npc('patron-drinker-c', 8, 12, 'down', { schedule: sitLoop('down') }),
  npc('patron-sailor-a', 9, 13, 'left', { dialogue: 'inn-talk-sailor', schedule: sitLoop('left') }),
  npc('patron-drinker-d', 21, 8, 'right', { schedule: sitLoop('right') }),
  npc('patron-drinker-e', 22, 9, 'up', { schedule: sitLoop('up') }),
  npc('patron-drinker-f', 23, 12, 'down', { schedule: sitLoop('down') }),
  npc('patron-drinker-g', 22, 13, 'right', { schedule: sitLoop('right') }),
  npc('patron-sailor-b', 14, 13, 'left', { schedule: sitLoop('left') }),
  // 站着的人（会让路）
  npc('patron-crowd-a', 12, 10, 'right', { schedule: idleLoop('right', 'down') }),
  npc('patron-crowd-b', 14, 9, 'left', { schedule: idleLoop('left', 'up', 3100) }),
  npc('patron-crowd-c', 20, 14, 'up', { schedule: idleLoop('up', 'left', 2700) }),
  npc('patron-crowd-d', 11, 14, 'right', { schedule: idleLoop('right', 'up', 3300) }),
];

/**
 * 坐着的人（休息姿态 sit，说话 sittalk，笑 laugh，举杯 drink）——只收录正式图集真有坐姿帧的角色：
 * 罗大湖、吴庸（sit/sittalk）与酒客 a–g（全坐姿图集）。站着喝的酒客 barfly a/b、水手 a/b、客人 crowd a–d 的图集只有站姿，
 * 所以他们站在桌边 / 酒台边喝（凳子已从 innMap 里撤掉）。
 */
export const SEATED_IDS = new Set<string>([
  CAST.luo, CAST.wu, 'patron-drinker-a', 'patron-drinker-b', 'patron-drinker-c', 'patron-drinker-d', 'patron-drinker-e',
  'patron-drinker-f', 'patron-drinker-g',
]);

/** 站着的客人（正式图集只有站姿：idle / talk / laugh 或 walk）。 */
export const STANDING_PATRON_IDS = ['patron-barfly-a', 'patron-barfly-b', 'patron-sailor-a', 'patron-sailor-b', 'patron-crowd-a', 'patron-crowd-b', 'patron-crowd-c', 'patron-crowd-d', 'patron-girl-a', 'patron-girl-b', 'patron-elder-a', 'patron-elder-b'] as const;

/**
 * 正式图集 key：`actor-<id>`（manifest 组 turtle-inn；`actor-npc-*` 模板在 shared 组）。
 * 与 characters 的 ACTOR_DEFS[id].texture 一致；纹理未加载时 characters 自动退回开发占位（devAtlas / 过程化贴图）。
 */
export function innActorTexture(id: string): string { return `actor-${id}`; }
/** 本场景会生成的所有角色 id（主角、赵呈博、名册 NPC）。 */
export const INN_ACTOR_IDS: readonly string[] = [CAST.player, CAST.zhao, ...INN_NPCS.map((n) => n.actor)];

/**
 * 环境闲聊：空间音效 key。目前用音频清单里已有的碰杯与哄笑（`sfx-cups`、`sfx-laughter-burst`）；
 * 专用的谈笑碎片 `inn-chatter-1…4` / `inn-laugh-1` 已列入 docs/asset-requests/inn-builder.md，到货后加进此表即可。
 */
export const CHATTER_SFX = ['sfx-cups', 'sfx-cups', 'sfx-laughter-burst'] as const;
/** 闲聊来源：会说话的桌子（像素中心）。 */
export const CHATTER_SOURCES = [feetOf(7, 9), feetOf(8, 13), feetOf(22, 8), feetOf(23, 13), feetOf(14, 13), feetOf(7, 6), feetOf(15, 17)];

/** 开发占位小标签（拉丁字母，不烙中文）。 */
const DEV_LABELS: Record<string, string> = {
  'inn-boss': 'BOS', 'inn-waiter': 'SRV',
  'patron-girl-a': 'G1', 'patron-girl-b': 'G2', 'patron-elder-a': 'E1', 'patron-elder-b': 'E2',
  'patron-drinker-a': 'D1', 'patron-drinker-b': 'D2', 'patron-drinker-c': 'D3', 'patron-drinker-d': 'D4', 'patron-drinker-e': 'D5', 'patron-drinker-f': 'D6', 'patron-drinker-g': 'D7',
  'patron-sailor-a': 'S1', 'patron-sailor-b': 'S2', 'patron-barfly-a': 'B1', 'patron-barfly-b': 'B2',
  'patron-crowd-a': 'C1', 'patron-crowd-b': 'C2', 'patron-crowd-c': 'C3', 'patron-crowd-d': 'C4',
};

/** 图集缺某状态时的回退（与管线 meta 一致）：坐姿酒客的一切都坐着；站着的人笑不成就站着；老板擦杯 / 小二端酒退回 idle。 */
const SEATED_FALLBACK: Readonly<Record<string, string>> = { drink: 'sit', laugh: 'sit', talk: 'sit', sittalk: 'sit' };
function innFallbackFor(id: string): Readonly<Record<string, string>> {
  if (SEATED_IDS.has(id)) return SEATED_FALLBACK;
  if (id === CAST.boss) return { wipe: 'idle' };
  if (id === CAST.waiter) return { carry: 'idle' };
  return { laugh: 'idle' };
}

/**
 * 老板、小二与全部客人的外观定义（主角、赵呈博、罗大湖、吴庸已在 ACTOR_DEFS 中）。
 * 正式资源 key：`actor-<id>`（图集，帧名规范见 src/characters/README.md）与 `portrait-<id>`；到货即按 key 替换。
 * characters-builder 已把同名条目登记进 ACTOR_DEFS，这里只在缺条目时补位（innRegister 只填空位）。
 */
export function innActorDefs(): Record<string, ActorDef> {
  const out: Record<string, ActorDef> = {};
  for (const [id, name] of Object.entries(DISPLAY_NAMES)) {
    if (id === CAST.player || id === CAST.zhao || id === CAST.luo || id === CAST.wu) continue;
    const [robe, skin, hair] = DEV_COLOURS[id] ?? [0x777777, 0xcfcfcf, 0x222222];
    out[id] = {
      texture: innActorTexture(id),
      displayName: name,
      portrait: `portrait-${id}`,
      speed: id === CAST.waiter ? 92 : 68,
      fallback: innFallbackFor(id),
      dev: { robe, accent: id.startsWith('patron-girl') ? 0x2b3a5c : 0xe2c48c, skin, hair, label: DEV_LABELS[id] ?? '?' },
    };
  }
  return out;
}
